/**
 * Sign-in.
 *
 * The device authorization grant (RFC 8628), which is the production flow: the
 * BFF starts it, WorkOS shows the code on its own origin, and this page never
 * sees a password, a one-time code, or an MFA prompt.
 *
 * Two iOS-specific behaviours are load-bearing:
 *
 * 1. **The popup window is claimed synchronously on click.** `device/start` talks
 *    to WorkOS and takes seconds; a `window.open` after that await falls outside
 *    Safari's transient-activation window and is silently blocked — on an iPad
 *    that means the sign-in page never appears at all.
 * 2. **Polling pauses while the tab is hidden and resumes on return.** WorkOS
 *    rate-limits the poll and answers `slow_down` if we are eager, and a
 *    backgrounded tab has its timers throttled anyway. Coming back to the tab
 *    checks immediately instead of waiting out the interval.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { AuthClient, DeviceAuthorization } from "../lib/auth";
import { IconComet } from "./icons";

export function LoginScreen({ auth, authMode }: { auth: AuthClient; authMode: "device" | "dev" }) {
  const [device, setDevice] = useState<DeviceAuthorization | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<string>("Waiting for approval…");
  const [devUser, setDevUser] = useState("dev-user");

  /** The claimed popup, held across renders so a poll can reuse it. */
  const signInWindow = useRef<Window | null>(null);
  const deadline = useRef(0);
  /** Current cadence in seconds; raised when WorkOS answers `slow_down`. */
  const interval = useRef(5);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** Set once the grant is approved, so a late poll cannot reopen the card. */
  const done = useRef(false);

  const stop = useCallback(() => {
    if (timer.current !== undefined) clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  /** Navigate the claimed window, or try once more if it is gone. */
  const openSignInPage = useCallback((url: string) => {
    const claimed = signInWindow.current;
    if (claimed !== null && !claimed.closed) {
      claimed.location.href = url;
      return;
    }
    try {
      const opened = window.open(url, "_blank");
      // `noopener` would make `window.open` return null and lose the handle.
      // Severing `opener` by hand gives the same protection.
      if (opened !== null) opened.opener = null;
      signInWindow.current = opened;
    } catch {
      // Blocked. The code and the manual button are still on screen, which is
      // the whole reason they are there.
    }
  }, []);

  const poll = useCallback(async () => {
    stop();
    if (done.current) return;
    if (Date.now() > deadline.current) {
      setDevice(undefined);
      setError("That sign-in expired before it was approved. Try again.");
      return;
    }
    let result;
    try {
      result = await auth.pollDeviceLogin();
    } catch (failure) {
      // A network blip mid-poll is not fatal: the authorization is still pending
      // server-side, so keep waiting rather than throwing the login away.
      setStatus(describe(failure));
      if (!done.current) {
        timer.current = setTimeout(() => void poll(), Math.max(1, interval.current) * 1000);
      }
      return;
    }
    if (result.status === "approved") {
      done.current = true;
      setDevice(undefined);
      return;
    }
    if (result.status === "pending") {
      // `slow_down` is a request to poll less often; honoring it is what keeps
      // the grant from being throttled for the rest of its life.
      if (result.interval !== undefined) interval.current = Math.max(interval.current, result.interval);
      setStatus("Waiting for approval…");
      timer.current = setTimeout(() => void poll(), Math.max(1, interval.current) * 1000);
      return;
    }
    if (result.status === "unknown") {
      setDevice(undefined);
      setError("That sign-in is no longer in progress. Try again.");
      return;
    }
    setDevice(undefined);
    setError(`Sign-in failed: ${result.error}`);
  }, [auth, stop]);

  const begin = useCallback(async () => {
    // Claim the popup first, synchronously, while the click's user activation
    // is still valid. Everything after this point is async.
    try {
      signInWindow.current = window.open("about:blank", "_blank");
      if (signInWindow.current !== null) signInWindow.current.opener = null;
    } catch {
      signInWindow.current = null;
    }
    setError(undefined);
    try {
      const authorization = await auth.beginDeviceLogin();
      deadline.current = Date.now() + authorization.expiresIn * 1000;
      interval.current = Math.max(1, authorization.interval);
      setDevice(authorization);
      setStatus("Waiting for approval…");
      openSignInPage(authorization.verificationUriComplete);
      void poll();
    } catch (failure) {
      signInWindow.current?.close();
      signInWindow.current = null;
      setError(describe(failure));
    }
  }, [auth, openSignInPage, poll]);

  // Returning to the tab checks immediately: on iPad this is the difference
  // between a login that completes when the human comes back and one that looks
  // stuck for the rest of the interval.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === "visible" && !done.current && timer.current !== undefined) {
        void poll();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [poll]);

  useEffect(() => stop, [stop]);

  return (
    <div className="flex min-h-full items-center justify-center bg-background p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <IconComet className="size-6 text-primary" />
          <h1 className="text-lg font-semibold tracking-tight">Zeron</h1>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Sign in to reach your workspace from this device.
        </p>

        {authMode === "dev" ? (
          <div className="mt-5 flex flex-col gap-2">
            <p className="text-xs text-muted-foreground">
              Local dev mode: no WorkOS involved. The user id becomes the bearer the edge accepts.
            </p>
            <label className="text-xs font-medium text-muted-foreground" htmlFor="dev-user">
              Dev user id
            </label>
            <input
              id="dev-user"
              value={devUser}
              onChange={(event) => setDevUser(event.target.value)}
              autoCapitalize="off"
              autoCorrect="off"
              className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-ring"
            />
            <button
              type="button"
              onClick={() => {
                void auth
                  .devLogin(devUser.trim() || "dev-user")
                  .catch((failure) => setError(describe(failure)));
              }}
              className="h-10 rounded-lg bg-primary text-sm font-medium text-primary-foreground"
            >
              Continue
            </button>
          </div>
        ) : device === undefined ? (
          <button
            type="button"
            onClick={() => void begin()}
            className="mt-5 h-10 w-full rounded-lg bg-primary text-sm font-medium text-primary-foreground"
          >
            Sign in
          </button>
        ) : (
          <div className="mt-5 flex flex-col gap-3">
            <p className="text-xs text-muted-foreground">Enter this code on the sign-in page:</p>
            <p className="rounded-lg bg-accent py-2 text-center font-mono text-lg tracking-[0.2em] text-accent-foreground">
              {device.userCode}
            </p>
            <button
              type="button"
              onClick={() => openSignInPage(device.verificationUriComplete)}
              className="h-10 rounded-lg bg-primary text-sm font-medium text-primary-foreground"
            >
              Open sign-in page
            </button>
            <p className="text-center text-xs text-muted-foreground">{status}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void poll()}
                className="h-9 flex-1 rounded-lg border border-border text-xs hover:bg-accent"
              >
                I have signed in
              </button>
              <button
                type="button"
                onClick={() => {
                  stop();
                  signInWindow.current?.close();
                  signInWindow.current = null;
                  setDevice(undefined);
                  setError(undefined);
                }}
                className="h-9 flex-1 rounded-lg text-xs text-muted-foreground hover:bg-accent"
              >
                Start over
              </button>
            </div>
          </div>
        )}

        {error !== undefined ? <p className="mt-3 text-xs text-error-foreground">{error}</p> : null}
      </div>
    </div>
  );
}

const describe = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);
